import React, { useEffect, useState } from "react";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import IconButton from "@mui/material/IconButton";
import AccountingOrgBar from "./AccountingOrgBar";
import { canLoadAccountingData } from "../useAccountingScope";
import { ACCOUNT_TYPES } from "../constants";

const AccountsTab = ({
	requiresOrganizationSelection,
	sessionOrganization,
	organizationId,
	setOrganizationId,
	fetchAccounts,
	createAccount,
	updateAccount,
	isSubmitting,
}) => {
	const [accounts, setAccounts] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [message, setMessage] = useState("");
	const [dialogOpen, setDialogOpen] = useState(false);
	const [editingId, setEditingId] = useState(null);
	const [form, setForm] = useState({
		code: "",
		name: "",
		account_type: "asset",
		description: "",
		is_postable: true,
		is_active: true,
	});

	const canLoad = canLoadAccountingData(requiresOrganizationSelection, organizationId);

	const load = async () => {
		if (!canLoad) {
			setAccounts([]);
			return;
		}
		setLoading(true);
		setError("");
		const result = await fetchAccounts(organizationId);
		if (result.success) {
			setAccounts(result.accounts);
		} else {
			setError(result.message);
			setAccounts([]);
		}
		setLoading(false);
	};

	useEffect(() => {
		load();
	}, [organizationId, requiresOrganizationSelection]);

	const openCreate = () => {
		setEditingId(null);
		setForm({
			code: "",
			name: "",
			account_type: "asset",
			description: "",
			is_postable: true,
			is_active: true,
		});
		setDialogOpen(true);
	};

	const openEdit = (row) => {
		setEditingId(row.id);
		setForm({
			code: row.code,
			name: row.name,
			account_type: row.account_type,
			description: row.description || "",
			is_postable: row.is_postable !== false,
			is_active: row.is_active !== false,
		});
		setDialogOpen(true);
	};

	const handleSubmit = async () => {
		if (!form.code.trim() || !form.name.trim()) {
			setMessage("Code and name are required.");
			return;
		}
		setMessage("");
		const createPayload = {
			code: form.code.trim(),
			name: form.name.trim(),
			account_type: form.account_type,
			description: form.description,
			is_postable: form.is_postable,
			is_active: form.is_active,
		};
		if (requiresOrganizationSelection) {
			createPayload.related_organization = organizationId;
		}
		const result = editingId
			? await updateAccount(editingId, {
					name: form.name,
					description: form.description,
					is_postable: form.is_postable,
					is_active: form.is_active,
			  })
			: await createAccount(createPayload);
		if (result.success) {
			setDialogOpen(false);
			load();
		} else {
			setMessage(result.message);
		}
	};

	return (
		<Box>
			<Box display="flex" gap={2} flexWrap="wrap" alignItems="center" mb={2}>
				<AccountingOrgBar
					requiresOrganizationSelection={requiresOrganizationSelection}
					organizationId={organizationId}
					onOrganizationChange={setOrganizationId}
					sessionOrganization={sessionOrganization}
				/>
				<Button
					variant="contained"
					startIcon={<AddIcon />}
					onClick={openCreate}
					disabled={!canLoad || isSubmitting}
				>
					Add account
				</Button>
			</Box>

			{error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
			{message && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMessage("")}>{message}</Alert>}

			{!canLoad ? (
				<Typography color="text.secondary">Select an organisation to manage accounts.</Typography>
			) : loading ? (
				<Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
			) : (
				<TableContainer component={Paper}>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Code</TableCell>
								<TableCell>Name</TableCell>
								<TableCell>Type</TableCell>
								<TableCell>Postable</TableCell>
								<TableCell>Active</TableCell>
								<TableCell align="right">Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{accounts.length === 0 ? (
								<TableRow>
									<TableCell colSpan={6} align="center">No accounts yet.</TableCell>
								</TableRow>
							) : (
								accounts.map((row) => (
									<TableRow key={row.id} hover>
										<TableCell sx={{ fontFamily: "monospace" }}>{row.code}</TableCell>
										<TableCell>{row.name}</TableCell>
										<TableCell>{row.account_type}</TableCell>
										<TableCell>{row.is_postable ? "Yes" : "No"}</TableCell>
										<TableCell>{row.is_active ? "Yes" : "No"}</TableCell>
										<TableCell align="right">
											<IconButton size="small" onClick={() => openEdit(row)}>
												<EditIcon fontSize="small" />
											</IconButton>
										</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</TableContainer>
			)}

			<Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>{editingId ? "Edit account" : "New account"}</DialogTitle>
				<DialogContent>
					<Box display="flex" flexDirection="column" gap={2} mt={1}>
						<TextField
							label="Code"
							value={form.code}
							onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
							disabled={Boolean(editingId)}
							fullWidth
						/>
						<TextField
							label="Name"
							value={form.name}
							onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
							fullWidth
						/>
						<FormControl fullWidth disabled={Boolean(editingId)}>
							<InputLabel>Account type</InputLabel>
							<Select
								label="Account type"
								value={form.account_type}
								onChange={(e) => setForm((f) => ({ ...f, account_type: e.target.value }))}
							>
								{ACCOUNT_TYPES.map((t) => (
									<MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
								))}
							</Select>
						</FormControl>
						<TextField
							label="Description"
							value={form.description}
							onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
							multiline
							rows={2}
							fullWidth
						/>
						<FormControlLabel
							control={
								<Switch
									checked={form.is_postable}
									onChange={(e) => setForm((f) => ({ ...f, is_postable: e.target.checked }))}
								/>
							}
							label="Postable"
						/>
						<FormControlLabel
							control={
								<Switch
									checked={form.is_active}
									onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
								/>
							}
							label="Active"
						/>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDialogOpen(false)}>Cancel</Button>
					<Button variant="contained" onClick={handleSubmit} disabled={isSubmitting}>
						{isSubmitting ? "Saving…" : "Save"}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default AccountsTab;
